import type { CSSProperties, ComponentPropsWithRef } from "react";
import { useEffect, useRef } from "react";
import Hls from "hls.js";
import { isHlsSupported } from "./utils";

export interface HlsHtmlVideoPlayerProps
  extends ComponentPropsWithRef<"video"> {
  description?: string;
  hasControls?: boolean;
  playbackUrl: string;
  shouldAutoPlay?: boolean;
  styles?: CSSProperties;
}

export const HlsHtmlVideoPlayer = ({
  playbackUrl,
  description = "",
  hasControls = true,
  children,
  styles,
  shouldAutoPlay = false,
  ...rest
}: HlsHtmlVideoPlayerProps) => {
  const hlsVideoRef = useRef<HTMLVideoElement>(null);
  const isSupported = isHlsSupported(playbackUrl);

  useEffect(() => {
    const video = hlsVideoRef.current;
    if (!video || !isSupported) {
      return;
    }
    const hls = new Hls();
    hls.loadSource(playbackUrl);
    hls.attachMedia(video);

    return () => {
      hls.destroy();
    };
  }, [playbackUrl, isSupported]);

  if (!isSupported) {
    return null;
  }

  return (
    <video
      {...rest}
      ref={hlsVideoRef}
      controls={hasControls}
      style={styles}
      autoPlay={shouldAutoPlay}
    >
      {description && <p className="wb-sr-only">{description}</p>}
      {children}
    </video>
  );
};
